import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { MODAL_CONFIRM } from "../../redux/reducers/modalReducer";
import { SAVE_PERSON_CONSENT } from "../../redux/reducers/personReducer";
import { useEffect, useState } from "react";
import { RESET_WARN_MESSAGES } from "../../redux/reducers/messageReducer";

export function ChangeConsentModal() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const consent = useSelector((state: any) => state.person.cardConsent);
  const searchForm = useSelector((state: any) => state.card.params);
  const optOut = useSelector((state: any) => state.person.cardConsent.readConsentOptOut);
  
  const [readConsentOptOut, setReadConsentOptOut] = useState<boolean>(false);
  const [readConsent, setReadConsent] = useState<boolean>(false);
  const [warningMessages, setWarningMessages] = useState<string[]>([]);
  const [ignoreWarning, setIgnoreWarning] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    setWarningMessages([]);
  }, [dispatch]);

  useEffect(() => {
    setReadConsent(consent?.readConsent);
  }, [consent]);

  useEffect(() => {
    setReadConsentOptOut(optOut);
  }, [optOut]);

  const consentAccept = () => {
    setReadConsentOptOut(false);
    setReadConsent(true);
  };

  const consentRemove = () => {
    setReadConsentOptOut(false);
    setReadConsent(false);
  };

  const consentOptOut = () => {
    let warn = false;
    let warnings: string[] = [];
    if (!ignoreWarning) {
      warn = true;
      warnings = [
        ...warnings,
        t("warn.denyConsent"),
      ];
    }
    setWarningMessages(warnings);
    
    if (!warn) {
      setReadConsentOptOut(true);
      setReadConsent(false);
    }
  };

  function handleChangeWarning(event: any) {
    setIgnoreWarning(event.target.checked);
  }

  const onSubmit = (values: any) => {
    const params = {
    carePrincipalId: searchForm.carePrincipalId,
    personId: searchForm.personId,
    cardTypeId: searchForm.cardTypeId,
    consent: {
      readConsent: readConsent,
        readConsentOptOut: readConsentOptOut,
        exportRestriction: null,
        recurringCard: null,
        sendPostToCarePrincipal: null,
        optOut: null,
        optIn: null,
        reason: values.reason,
        },
      };
    if (warningMessages.length === 0 || ignoreWarning) {
      dispatch({ type: SAVE_PERSON_CONSENT, params });
      dispatch({ type: RESET_WARN_MESSAGES });
      dispatch({ type: MODAL_CONFIRM });
    }
  };

  let consentActionButtons;
  if (optOut) {
    consentActionButtons = (
      <Stack direction="row" spacing={1}>
        <Button type="submit" onClick={() => consentRemove()} variant="contained" color="primary">
          {t("remove")}
        </Button>
        <Button type="submit" onClick={() => consentAccept()} variant="contained" color="success">
          {t("accept")}
        </Button>
      </Stack>
    );
  } else if (consent.readConsent) {
    consentActionButtons = (
      <Stack direction="column" spacing={1}>
        <Stack sx={{ justifyContent: "flex-end" }} direction="row" spacing={1}>
          <Button  type="submit" onClick={() => consentOptOut()} variant="contained" color="error">
            {t("deny")}
          </Button>
          {warningMessages.length === 0 && (
          <Button type="submit" onClick={() => consentRemove()} variant="contained" color="primary">
            {t("remove")}
          </Button>
          )}
        </Stack>
      </Stack>
    );
  } else {
    consentActionButtons = (
      <Stack direction="column" spacing={1}>
        <Stack sx={{ justifyContent: "flex-end" }} direction="row" spacing={1}>
          <Button type="submit" onClick={() => consentOptOut()} variant="contained" color="error">
            {t("deny")}
          </Button>
          {warningMessages.length === 0 && (
          <Button type="submit" onClick={() => consentAccept()} variant="contained" color="success">
            {t("accept")}
          </Button>
          )}    
        </Stack>
      </Stack>
    );
  }


  let consentDialogText: string;
  if (readConsentOptOut) {
    consentDialogText = t("searchCard.modals.deniedConsent");
  } else if (consent.readConsent) {
    consentDialogText = t("searchCard.modals.removeConsent");
  } else {
    consentDialogText = t("searchCard.modals.chooseReadConsent");
  }

  return (
    <Dialog
      fullWidth={true}
      maxWidth={"sm"}
      open={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={1} sx={{ padding: "15px", maxWidth: "40rem" }}>
          <DialogTitle sx={{ padding: "3px 3px" }} id="alert-dialog-title">
            {t("searchCard.modals.readConsent")}
          </DialogTitle>
          <DialogContent sx={{ padding: "15px 3px" }}>
            <DialogContentText>
              {consentDialogText}
            </DialogContentText>
          </DialogContent>
          <Controller
            name="reason"
            control={control}
            render={() => (
              <TextField
                id="reason"
                error={errors.reason ? true : false}
                size="small"
                label={t("searchCard.manageTxns.deleteReason")}
                {...register("reason")}
              />
            )}
          />
          <DialogActions sx={{ padding: "5px 0px 0px 0px" }}>
            <Stack direction="column" spacing={1} sx={{ width: "100%" }}>
              {warningMessages.length > 0 ? (
              <Stack direction="row" spacing={1}>
                {warningMessages.map((message) => (
                  <Alert sx={{ padding: "3px 3px" }} key={message} severity="error">
                    {message}
                  </Alert>
                ))}
                <FormControlLabel
                  label="Godkänn"
                control={
                  <Checkbox
                    checked={ignoreWarning}
                    onChange={handleChangeWarning}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
                />
              </Stack>
            ) : null}
              <Stack direction="row" spacing={1} display="flex" justifyContent="space-between" sx={{ width: "100%" }}>
                  <Box justifyContent="flex-start">
                    <Button
                      variant="contained"
                      onClick={() => dispatch({ type: "MODAL_CANCEL" })}
                    >
                      {t("cancel")}
                    </Button>
                  </Box>
                  <Box justifyContent="flex-end">
                    {consentActionButtons}
                  </Box>
              </Stack>
            </Stack>
          </DialogActions>
        </Stack>
      </form>
    </Dialog>
  );
}
